import { useEffect, useMemo, useState } from 'react';
import {
    Button,
    ButtonVariant,
    Dialog,
    DirectionLeftIcon,
    DirectionUpIcon,
    Divider,
    DuplicatedInvoiceChip,
    formatCurrency,
    PreviewFile,
    useDialog,
} from '@barkibu/noma-commons';
import InvoiceForm from './invoice-form/InvoiceForm';
import CostLine from '../../../../domain/entities/CostLine';
import Invoice from '../../../../domain/entities/Invoice';
import { InvoicesDetails as InvoicesDetailsDomain } from '../../../../domain/entities/InvoicesDetails';
import { useModuleTranslation } from '../../../../utils/useModuleTranslation';
import ExternalReviewTag from './invoice-header/external-review-tag/ExternalReviewTag';
import './invoice-body.scss';

export interface InvoiceBodyProps {
    invoicesDetails: InvoicesDetailsDomain;
    readOnly?: boolean;
    onUpdateClinic: (key: string, clinic: string) => void;
    onUpdateCostLines: (key: string, costLines: CostLine[]) => void;
    onUpdateDate: (key: string, date: Date) => void;
    onUpdateDiscount: (key: string, discount: number) => void;
    onUpdateNumber: (key: string, number: string) => void;
    onUpdateTaxIdNumber: (key: string, taxIdNumber: string) => void;
    onDeleteInvoice?: (invoiceKey: string) => Promise<void>;
    claimRequestKey?: string;
}

const getInvoiceTotalRequested = (invoice: Invoice) => {
    const totalRequested =
        invoice.costLines.reduce((total, costLine) => {
            const lineCost = costLine.priceInUnits() * (1 + costLine.VAT) * costLine.units;
            return total + Math.round(lineCost * 100) / 100;
        }, 0) -
        (invoice.discountInCents ?? 0) / 100;

    return Math.max(Math.round(totalRequested * 100) / 100, 0);
};

const getDocumentIdentifier = (documentKey: string | undefined, index: number) => documentKey ?? `document-${index}`;

const getInvoiceIdentifier = (invoice: Invoice, index: number, documentIdentifier: string) =>
    invoice.key ?? `${documentIdentifier}-invoice-${index}`;

const InvoiceBody = ({
    invoicesDetails,
    readOnly = false,
    onUpdateClinic,
    onUpdateCostLines,
    onUpdateDate,
    onUpdateDiscount,
    onUpdateNumber,
    onUpdateTaxIdNumber,
    onDeleteInvoice,
    claimRequestKey,
}: InvoiceBodyProps) => {
    const { t } = useModuleTranslation();
    const { dialogOptions, openDialog, closeDialog } = useDialog();
    const groups = useMemo(() => invoicesDetails.groupedBySupportingDocument(), [invoicesDetails]);
    const groupCount = groups.length;

    const [currentDocumentIndex, setCurrentDocumentIndex] = useState<number>(0);
    const [selectedDocumentIndex, setSelectedDocumentIndex] = useState<number>(0);
    const [expandedInvoiceKeysByDocument, setExpandedInvoiceKeysByDocument] = useState<Record<string, string>>({});
    const [pendingDeletion, setPendingDeletion] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (selectedDocumentIndex >= groupCount && groupCount > 0) {
            const lastIndex = groupCount - 1;
            setSelectedDocumentIndex(lastIndex);
            setCurrentDocumentIndex(lastIndex);
        }
    }, [groupCount, selectedDocumentIndex]);

    useEffect(() => {
        setCurrentDocumentIndex(selectedDocumentIndex);
    }, [selectedDocumentIndex]);

    if (groupCount === 0) {
        return <h3>{t('invoice.nothing_to_digitise')}</h3>;
    }

    const selectedGroup = groups[Math.min(selectedDocumentIndex, groupCount - 1)];
    const selectedDocument = selectedGroup.supportingDocument;
    const invoiceCount = selectedGroup.invoices.length;
    const allowDeletion = onDeleteInvoice != null && invoiceCount > 1;
    const selectedDocumentIdentifier = getDocumentIdentifier(selectedDocument.key, selectedDocumentIndex);
    const rememberedExpandedInvoiceKey = expandedInvoiceKeysByDocument[selectedDocumentIdentifier];
    const expandedInvoiceKey =
        rememberedExpandedInvoiceKey != null &&
        selectedGroup.invoices.some(
            (invoice, index) =>
                getInvoiceIdentifier(invoice, index, selectedDocumentIdentifier) === rememberedExpandedInvoiceKey
        )
            ? rememberedExpandedInvoiceKey
            : getInvoiceIdentifier(selectedGroup.invoices[0], 0, selectedDocumentIdentifier);

    const requestDelete = (invoiceKey: string) => {
        if (onDeleteInvoice == null) return;
        setPendingDeletion(invoiceKey);
    };

    const expandInvoice = (invoiceIdentifier: string) => {
        setExpandedInvoiceKeysByDocument((current) => ({
            ...current,
            [selectedDocumentIdentifier]: invoiceIdentifier,
        }));
    };

    const confirmDelete = async () => {
        if (pendingDeletion == null || onDeleteInvoice == null) return;
        const invoiceKey = pendingDeletion;
        setPendingDeletion(undefined);
        try {
            await onDeleteInvoice(invoiceKey);
        } catch (e) {
            openDialog(t('invoice.delete_error'));
        }
    };

    return (
        <>
            <div className="invoice-details--body">
                <div className="document-section--header">
                    <div className="document-section--carrousel">
                        <Button
                            icon={DirectionLeftIcon}
                            variantName={ButtonVariant.BASIC_INFO}
                            onClick={() => setSelectedDocumentIndex(selectedDocumentIndex - 1)}
                            disabled={selectedDocumentIndex == 0}
                            accessibilityText={t('invoice.go_to_previous_document')}
                        />
                        <h2 className={'title'}>
                            {t('invoice.document_title', {
                                current: selectedDocumentIndex + 1,
                                totalCount: groupCount,
                            })}
                        </h2>
                        <Button
                            icon={DirectionUpIcon}
                            variantName={ButtonVariant.BASIC_INFO}
                            onClick={() => setSelectedDocumentIndex(selectedDocumentIndex + 1)}
                            disabled={selectedDocumentIndex >= groupCount - 1}
                            accessibilityText={t('invoice.go_to_next_document')}
                        />
                    </div>
                </div>
                <Divider />
                <div className="invoice-details--body-content">
                    <div className="invoice-details--body-content--viewer">
                        <PreviewFile url={selectedDocument.location!} />
                    </div>
                    <div className="invoice-details--body-content--information">
                        {selectedGroup.invoices.map((invoice, index) => {
                            const invoiceIdentifier = getInvoiceIdentifier(invoice, index, selectedDocumentIdentifier);
                            const isExpanded = invoiceIdentifier === expandedInvoiceKey;
                            const title =
                                invoiceCount > 1
                                    ? t('invoice.position_in_document', {
                                          current: index + 1,
                                          totalCount: invoiceCount,
                                      })
                                    : undefined;

                            return (
                                <div
                                    className={`invoice-card ${isExpanded ? 'invoice-card--expanded' : 'invoice-card--collapsed'}`}
                                    key={invoiceIdentifier}
                                >
                                    {isExpanded ? (
                                        <InvoiceForm
                                            invoice={invoice}
                                            readOnly={readOnly}
                                            onUpdateClinic={onUpdateClinic}
                                            onUpdateCostLines={onUpdateCostLines}
                                            onUpdateDate={onUpdateDate}
                                            onUpdateDiscount={onUpdateDiscount}
                                            onUpdateNumber={onUpdateNumber}
                                            onUpdateTaxIdNumber={onUpdateTaxIdNumber}
                                            onDeleteInvoice={allowDeletion ? requestDelete : undefined}
                                            claimRequestKey={claimRequestKey}
                                            title={title}
                                        />
                                    ) : (
                                        <button
                                            type="button"
                                            className="invoice-card__summary"
                                            onClick={() => expandInvoice(invoiceIdentifier)}
                                            aria-expanded={false}
                                        >
                                            <div className="invoice-card__summary-topline">
                                                <div className="invoice-card__summary-title-group">
                                                    {title != null && (
                                                        <span className="invoice-card__summary-eyebrow">{title}</span>
                                                    )}
                                                </div>
                                                <div className="invoice-card__summary-tags">
                                                    <DuplicatedInvoiceChip
                                                        taxIdNumber={invoice.taxIdNumber}
                                                        number={invoice.number}
                                                        date={invoice.date}
                                                        claimRequestKey={claimRequestKey}
                                                    />
                                                    <ExternalReviewTag externalReview={invoice.externalReview} />
                                                </div>
                                            </div>
                                            <div className="invoice-card__summary-grid">
                                                {invoice.number != null && (
                                                    <div className="invoice-card__summary-item">
                                                        <span className="invoice-card__summary-label">
                                                            {t('invoice.number')}
                                                        </span>
                                                        <span>{invoice.number}</span>
                                                    </div>
                                                )}
                                                {invoice.date != null && (
                                                    <div className="invoice-card__summary-item">
                                                        <span className="invoice-card__summary-label">
                                                            {t('invoice.date')}
                                                        </span>
                                                        <span>{invoice.date.toLocaleDateString()}</span>
                                                    </div>
                                                )}
                                                {invoice.taxIdNumber != null && (
                                                    <div className="invoice-card__summary-item">
                                                        <span className="invoice-card__summary-label">
                                                            {t('invoice.cif')}
                                                        </span>
                                                        <span>{invoice.taxIdNumber}</span>
                                                    </div>
                                                )}
                                                {invoice.costLines.length > 0 && (
                                                    <div className="invoice-card__summary-item">
                                                        <span className="invoice-card__summary-label">
                                                            {t('invoice.total_requested')}
                                                        </span>
                                                        <span>{formatCurrency(getInvoiceTotalRequested(invoice), invoice.currency)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            <Dialog
                isOpen={currentDocumentIndex != selectedDocumentIndex}
                text={t('invoice.confirm_exit_with_changes')}
                actions={[
                    {
                        text: t('modal.continue', { ns: 'commons' }),
                        variantName: ButtonVariant.DANGER,
                        onClick: () => {
                            setCurrentDocumentIndex(selectedDocumentIndex);
                        },
                    },
                    {
                        text: t('modal.cancel', { ns: 'commons' }),
                        variantName: ButtonVariant.SECONDARY,
                        onClick: () => {
                            setSelectedDocumentIndex(currentDocumentIndex);
                        },
                    },
                ]}
                onClose={() => {
                    setSelectedDocumentIndex(currentDocumentIndex);
                }}
            />
            <Dialog
                isOpen={pendingDeletion != null}
                text={t('invoice.delete_confirmation')}
                actions={[
                    {
                        text: t('invoice.delete'),
                        variantName: ButtonVariant.DANGER,
                        onClick: confirmDelete,
                    },
                    {
                        text: t('modal.cancel', { ns: 'commons' }),
                        variantName: ButtonVariant.SECONDARY,
                        onClick: () => setPendingDeletion(undefined),
                    },
                ]}
                onClose={() => setPendingDeletion(undefined)}
            />
            <Dialog
                isOpen={dialogOptions.isOpen}
                text={dialogOptions.text}
                details={dialogOptions.details}
                onClose={() => closeDialog()}
                actions={[
                    {
                        text: t('modal.close', { ns: 'commons' }),
                        variantName: ButtonVariant.PRIMARY,
                        onClick: () => closeDialog(),
                    },
                ]}
            />
        </>
    );
};

export default InvoiceBody;
